import React, { useEffect, useRef, useState } from "react";
import { useFormik } from "formik";
import * as Yup from "yup"; // Optional for validation
import { IoIosMenu } from "react-icons/io";
import Arrow from "../../assets/images/icon/Arrow.png";
import Menu from "../../assets/images/icon/Options.png";
import UploadIcon from "../../assets/images/icon/Upload.png";
import { toastError } from "../../components/ToastNotification";
import TrainingService from "../../services/training-group";
import Loader from "../../assets/images/icon/LinkoSpinner.gif";
import ChatService from "../../services/chat";
import { BadgeCheckIcon, Chrome, Copy, Send, Share } from "lucide-react";
import ReactMarkdown from "react-markdown";
import DOMPurify from "dompurify";
import copy from "copy-to-clipboard";
// import { TypingResponse } from "../../components/TypingEffect";
import Logo from "../../assets/images/logo/logo-icon.jpg";
import UserProfile from "../../assets/images/icon/users.jpg";
import ChatLoader from "../../assets/images/icon/loader-one.gif";
import { useDispatch, useSelector } from "react-redux";
import { setInitial } from "../../store/slices/chatSlice";
import { CiRedo } from "react-icons/ci";
import { useLocation } from "react-router-dom";
import { BsFillStopCircleFill } from "react-icons/bs";
import remarkBreaks from "remark-breaks";
import remarkGfm from "remark-gfm"; // Enables tables, strikethrough, etc.
import rehypeRaw from "rehype-raw"; // Allows HTML inside Markdown

const renderContent = (content) => {
  if (!content) return null;

  // Helper function to normalize and enhance markdown for consistent rendering
  const preprocessMarkdown = (text) => {
    // Clone the text to avoid modifying the original
    let processed = text;

    // 1. Normalize line endings for consistent processing
    processed = processed.replace(/\r\n/g, "\n");

    // 2. Convert asterisk/plus bullet points to dash for consistency
    // This handles various indentation levels and formats
    processed = processed.replace(/^(\s*)[\*\+]\s+/gm, "$1- ");

    // 3. Ensure headers have proper spacing (# Title instead of #Title)
    processed = processed.replace(/^(#{1,6})([^#\s\n])/gm, "$1 $2");

    // 4. Fix missing blank lines around block elements for proper markdown parsing

    // Before and after lists
    // Add blank line before a list if preceded by a non-list item
    processed = processed.replace(
      /^(?!\s*[-*+])(.+)\n(\s*[-*+]\s+)/gm,
      "$1\n\n$2"
    );
    // Add blank line after a list if followed by a non-list item
    processed = processed.replace(
      /^(\s*[-*+]\s+.+)\n(?!\s*[-*+]|$)(.)/gm,
      "$1\n\n$2"
    );

    // Before and after headings
    // Add blank line before a heading if not already there
    processed = processed.replace(/^(.+)\n(#{1,6}\s+)/gm, "$1\n\n$2");
    // Add blank line after a heading if not already there
    processed = processed.replace(
      /^(#{1,6}\s+.+)\n(?!$|#{1,6}\s+)(.)/gm,
      "$1\n\n$2"
    );

    // Before and after code blocks
    processed = processed.replace(/^(.+)\n(```)/gm, "$1\n\n$2");
    processed = processed.replace(/^(```.*)(\n)(?!$|```)/gm, "$1\n\n$2");

    // 5. Ensure proper formatting of bold, italic, and code spans
    // Add spaces around inline code if missing
    processed = processed.replace(/([^\s`])`([^`]+)`([^\s`])/g, "$1 `$2` $3");

    // 6. Fix indentation in nested lists
    // This makes sure nested lists have proper indentation
    const lines = processed.split("\n");
    let inList = false;
    let listLevel = 0;

    for (let i = 0; i < lines.length; i++) {
      // Check if current line is a list item
      const listMatch = lines[i].match(/^(\s*)([-*+]|\d+\.)\s/);

      if (listMatch) {
        // We're in a list
        const currentIndent = listMatch[1].length;

        if (!inList) {
          // Starting a new list
          inList = true;
          listLevel = currentIndent;
        } else if (currentIndent > listLevel) {
          // This is a nested list item
          // Ensure proper indentation (at least 2 spaces more than parent)
          const properIndent = listLevel + 2;
          if (currentIndent < properIndent) {
            lines[i] = " ".repeat(properIndent) + lines[i].trim();
          }
        }
      } else if (lines[i].trim() === "") {
        // Empty line could mean end of list
        inList = false;
      }
    }

    processed = lines.join("\n");

    return processed;
  };

  // Preprocess the content to fix markdown formatting issues
  const processedContent = preprocessMarkdown(content);

  // Comprehensive regex to detect various markdown elements
  const hasMarkdown =
    /(\*\*|__|~~|```|`|\[.*?\]\(.*?\)|^[-*+]\s|^#{1,6}\s|^\d+\.\s|<[a-z][a-z0-9]*(\s|>)|<\/[a-z][a-z0-9]*>)/im.test(
      processedContent
    );

  // Handle Markdown content
  if (hasMarkdown) {
    return (
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        rehypePlugins={[rehypeRaw]}
        components={{
          p: ({ node, ...props }) => (
            <p style={{ marginBottom: "1rem" }} {...props} />
          ),
          ul: ({ node, ...props }) => (
            <ul
              style={{
                marginBottom: "1rem",
                marginLeft: "1.5rem",
                listStyleType: "disc",
              }}
              {...props}
            />
          ),
          ol: ({ node, ...props }) => (
            <ol
              style={{
                marginBottom: "1rem",
                marginLeft: "1.5rem",
                listStyleType: "decimal",
              }}
              {...props}
            />
          ),
          li: ({ node, children, ...props }) => {
            // Check if this list item contains a nested list
            const hasNestedList = React.Children.toArray(children).some(
              (child) =>
                React.isValidElement(child) &&
                (child.type === "ul" || child.type === "ol")
            );
            return (
              <li
                style={{ marginBottom: hasNestedList ? "0.25rem" : "0.5rem" }}
                {...props}
              >
                {children}
              </li>
            );
          },
          h1: ({ node, ...props }) => (
            <h1
              style={{
                marginTop: "1.5rem",
                marginBottom: "1rem",
                fontSize: "2rem",
                fontWeight: "bold",
              }}
              {...props}
            />
          ),
          h2: ({ node, ...props }) => (
            <h2
              style={{
                marginTop: "1.5rem",
                marginBottom: "1rem",
                fontSize: "1.75rem",
                fontWeight: "bold",
              }}
              {...props}
            />
          ),
          h3: ({ node, ...props }) => (
            <h3
              style={{
                marginTop: "1.25rem",
                marginBottom: "0.75rem",
                fontSize: "1.5rem",
                fontWeight: "bold",
              }}
              {...props}
            />
          ),
          h4: ({ node, ...props }) => (
            <h4
              style={{
                marginTop: "1rem",
                marginBottom: "0.5rem",
                fontSize: "1.25rem",
                fontWeight: "bold",
              }}
              {...props}
            />
          ),
          h5: ({ node, ...props }) => (
            <h5
              style={{
                marginTop: "0.75rem",
                marginBottom: "0.5rem",
                fontSize: "1.1rem",
                fontWeight: "bold",
              }}
              {...props}
            />
          ),
          h6: ({ node, ...props }) => (
            <h6
              style={{
                marginTop: "0.75rem",
                marginBottom: "0.5rem",
                fontSize: "1rem",
                fontWeight: "bold",
              }}
              {...props}
            />
          ),
          a: ({ node, ...props }) => (
            <a
              style={{ color: "#0366d6", textDecoration: "none" }}
              {...props}
            />
          ),
          blockquote: ({ node, ...props }) => (
            <blockquote
              style={{
                borderLeft: "4px solid #dfe2e5",
                paddingLeft: "1rem",
                marginLeft: "0",
                marginRight: "0",
                marginTop: "1rem",
                marginBottom: "1rem",
                color: "#6a737d",
              }}
              {...props}
            />
          ),
          code: ({ node, inline, className, children, ...props }) => {
            // Extract language from className if present
            const match = /language-(\w+)/.exec(className || "");
            return inline ? (
              <code
                style={{
                  background: "#f1f1f1",
                  padding: "0.2em 0.4em",
                  borderRadius: "3px",
                  fontFamily: "monospace",
                  fontSize: "0.85em",
                }}
                {...props}
              >
                {children}
              </code>
            ) : (
              <code
                className={className}
                style={{
                  display: "block",
                  background: "#f6f8fa",
                  padding: "1rem",
                  borderRadius: "5px",
                  fontFamily: "monospace",
                  fontSize: "0.85em",
                  overflowX: "auto",
                  marginTop: "1rem",
                  marginBottom: "1rem",
                }}
                {...props}
              >
                {children}
              </code>
            );
          },
          pre: ({ node, ...props }) => (
            <pre
              style={{ background: "transparent", padding: "0", margin: "0" }}
              {...props}
            />
          ),
          table: ({ node, ...props }) => (
            <div
              style={{
                overflowX: "auto",
                marginTop: "1rem",
                marginBottom: "1rem",
              }}
            >
              <table
                style={{
                  borderCollapse: "collapse",
                  width: "100%",
                  border: "1px solid #dfe2e5",
                }}
                {...props}
              />
            </div>
          ),
          thead: ({ node, ...props }) => (
            <thead style={{ background: "#f6f8fa" }} {...props} />
          ),
          th: ({ node, ...props }) => (
            <th
              style={{
                padding: "0.5rem 1rem",
                border: "1px solid #dfe2e5",
                fontWeight: "600",
              }}
              {...props}
            />
          ),
          td: ({ node, ...props }) => (
            <td
              style={{
                padding: "0.5rem 1rem",
                border: "1px solid #dfe2e5",
              }}
              {...props}
            />
          ),
          hr: ({ node, ...props }) => (
            <hr
              style={{
                height: "0.25em",
                padding: "0",
                margin: "1.5rem 0",
                background: "#e1e4e8",
                border: "0",
              }}
              {...props}
            />
          ),
        }}
      >
        {processedContent}
      </ReactMarkdown>
    );
  }

  // Handle HTML code blocks (```html ... ```)
  const codeBlockMatch = processedContent.match(/^```html\n([\s\S]*?)\n```$/);
  if (codeBlockMatch) {
    const htmlContent = codeBlockMatch[1].trim();
    const sanitizedHtml = DOMPurify.sanitize(htmlContent);
    return <div dangerouslySetInnerHTML={{ __html: sanitizedHtml }} />;
  }

  // Handle regular HTML content
  if (processedContent.includes("<") && processedContent.includes(">")) {
    const sanitizedHtml = DOMPurify.sanitize(processedContent);
    return <div dangerouslySetInnerHTML={{ __html: sanitizedHtml }} />;
  }

  // Default: Render plain text while preserving spaces & new lines
  return <p style={{ whiteSpace: "pre-wrap" }}>{processedContent}</p>;
};

const Chat = () => {
  const location = useLocation();
  const abortControllerRef = useRef(null);
  const [userDetails, setUserDetails] = useState("");
  const user = useSelector((state) => state.user);
  const chat = useSelector((state) => state.chat);
  const dispatch = useDispatch();
  const dropdownRef = useRef(null);
  const textareaRef = useRef(null);
  const messageEndRef = useRef(null);
  const [loading, setLoading] = useState(true);
  const [isOpen, setIsOpen] = useState(false);
  const [options, setOptions] = useState([]);
  const [selectedOptions, setSelectedOptions] = useState("");
  const [suggestion, setsuggestion] = useState("");
  const [messages, setMessages] = useState(() => {
    try {
      const saved = localStorage.getItem("linko_chat_messages");
      return saved ? JSON.parse(saved) : [];
    } catch {
      return [];
    }
  });
  const [initialRender, setInitialRender] = useState(() => {
    try {
      const saved = localStorage.getItem("linko_chat_messages");
      const parsed = saved ? JSON.parse(saved) : [];
      return parsed.length === 0;
    } catch {
      return true;
    }
  });
  const [isLoading, setIsLoading] = useState(false);
  const [copiedIndex, setCopiedIndex] = useState("");
  const [complete, setComplete] = useState(false);
  const [isClick, setIsClick] = useState(false);
  console.log("🚀  Chat  isClick:", isClick);
  const toggleDropdown = () => {
    if (options?.length === 0) {
      setIsOpen(false);
    } else {
      setIsOpen(!isOpen);
    }
  };

  const handleCopyClick = (text, index) => {
    console.log("🚀 handleCopyClick text:", text);

    setCopiedIndex(index);

    // Create a temporary element to extract text content from the HTML string
    const tempElement = document.createElement("div");
    tempElement.innerHTML = text; // Convert the HTML string into an actual DOM element
    const textToCopy = tempElement.textContent.trim(); // Extract only plain text
    const cleanedText = textToCopy.replace(/```html|```/g, "").trim(); // Remove backticks

    navigator.clipboard
      .writeText(cleanedText) // Copy only the cleaned text
      .then(() => {
        setTimeout(() => {
          setCopiedIndex(""); // Reset index after timeout
        }, 1000);
      })
      .catch((err) => console.error("Failed to copy: ", err));
  };

  const handleOptionClick = (option) => {
    setSelectedOptions(option); // Add option to selected options
    setIsOpen(false);
    setsuggestion(option);
  };

  // Remove selected option
  const handleRemoveOption = () => {
    setsuggestion("");
    setSelectedOptions(""); // Remove selected option
  };

  const getAllSuggettions = async () => {
    setLoading(true);
    try {
      const response = await TrainingService?.getAllSuggettions();
      if (response) {
        setOptions(
          response?.data?.suggestions
            ? response?.data?.suggestions?.suggestion
            : []
        );
      }
    } catch (error) {
      toastError(error?.response?.data?.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setLoading(true);
    getAllSuggettions();
  }, []);

  useEffect(() => {
    const data = localStorage.getItem("linko_admin_details");
    setUserDetails(JSON.parse(data));
  }, []);

  const handleSubmit = async (values) => {
    setIsClick(false);
    setIsUserScrolledUp(false);
    if (isLoading || complete) {
      return;
    }
    if (!values.input) {
      return;
    }
    dispatch(setInitial(false));
    setInitialRender(false);
    setIsLoading(true);
    formik.resetForm();
    const newMessage = { type: "user", content: values?.input };
    setMessages([...messages, newMessage]);
    abortControllerRef.current = new AbortController(); // Create a new AbortController
    const signal = abortControllerRef.current.signal;
    try {
      const body = {
        inputText: values.input,
        suggestion: suggestion,
      };
      const response = await ChatService?.createChat(body, { signal });
      if (response) {
        setInitialRender(false);
        const botResponse = {
          type: "bot",
          content: response?.data?.data,
        };
        setMessages((prev) => [...prev, botResponse]);
        setsuggestion("");
      }
    } catch (error) {
      console.log("🚀  handleSubmit  error:", error);
      if (error?.message === "canceled") {
        return;
      }
      // setInitialRender(true);
      toastError(error?.response?.data?.message);
    } finally {
      // setInitialRender(false);
      setIsLoading(false);
    }
  };

  // Initialize Formik
  const formik = useFormik({
    initialValues: {
      input: "", // Initialize the input field
    },
    validationSchema: Yup.object({
      input: Yup.string(),
      /* .required("This field is required")
        .max(255, "Input is too long") */
    }),
    onSubmit: handleSubmit,
  });

  // persist messages to localStorage on every change
  useEffect(() => {
    localStorage.setItem("linko_chat_messages", JSON.stringify(messages));
  }, [messages]);

  useEffect(() => {
    if (messageEndRef.current) {
      messageEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [messages]);

  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = "auto";
      textareaRef.current.style.height = `${textareaRef.current.scrollHeight}px`;
    }
  }, [formik.values.input]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false); // Close the dropdown if clicked outside
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handleNewChatClick = () => {
    formik.setFieldValue("input", "");
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    abortControllerRef.current = new AbortController();
    localStorage.removeItem("linko_chat_messages");
    dispatch(setInitial(true));
    setInitialRender(true);
    setMessages([]);
    setComplete(false);
    setIsLoading(false);
    setIsUserScrolledUp(false);
    setsuggestion("");
    setSelectedOptions("");
  };

  // In your parent component where messages are rendered
  const messageContainerRef = useRef(null);

  const chatContainerRef = useRef(null);
  const [userScrolledUp, setUserScrolledUp] = useState(false);
  const [autoScrollPaused, setAutoScrollPaused] = useState(false);

  // Add this useEffect for handling scroll detection
  useEffect(() => {
    const container = chatContainerRef.current;
    if (!container) return;

    const handleScroll = () => {
      const { scrollTop, scrollHeight, clientHeight } = container;
      const distanceFromBottom = scrollHeight - scrollTop - clientHeight;

      // Consider user scrolled up if they're more than 100px from bottom
      const isScrolledUp = distanceFromBottom > 100;

      if (isScrolledUp && !userScrolledUp) {
        setUserScrolledUp(true);
        setAutoScrollPaused(true);
      } else if (!isScrolledUp && userScrolledUp) {
        setUserScrolledUp(false);
      }
    };

    container.addEventListener("scroll", handleScroll);
    return () => container.removeEventListener("scroll", handleScroll);
  }, [userScrolledUp]);

  // Add listeners for typing events
  useEffect(() => {
    const handleTypingStarted = () => {
      if (!autoScrollPaused) {
        scrollToBottom();
      }
    };

    const handleTypingFinished = () => {
      if (!autoScrollPaused) {
        scrollToBottom();
      }
      // Reset auto-scrolling when typing finishes
      setAutoScrollPaused(false);
    };

    const scrollToBottom = () => {
      const container = chatContainerRef.current;
      const messageEnd = messageEndRef.current;

      if (container && messageEnd) {
        messageEnd.scrollIntoView({ behavior: "smooth" });
      }
    };

    window.addEventListener("typingStarted", handleTypingStarted);
    window.addEventListener("typingFinished", handleTypingFinished);

    return () => {
      window.removeEventListener("typingStarted", handleTypingStarted);
      window.removeEventListener("typingFinished", handleTypingFinished);
    };
  }, [autoScrollPaused]);

  // Add this useEffect for handling auto-scrolling on new messages
  useEffect(() => {
    // Auto-scroll when new messages arrive (not during typing)
    if (messages.length > 0 && !isLoading && !complete) {
      const container = chatContainerRef.current;
      const messageEnd = messageEndRef.current;

      if (container && messageEnd && !autoScrollPaused) {
        messageEnd.scrollIntoView({ behavior: "smooth" });
      }
    }
  }, [messages, isLoading, complete, autoScrollPaused]);

  const handleScroll = () => {
    if (!messageContainerRef.current) return;

    const isAtBottom =
      Math.abs(
        messageContainerRef.current.scrollHeight -
          messageContainerRef.current.clientHeight -
          messageContainerRef.current.scrollTop
      ) < 10;

    setIsUserScrolledUp(!isAtBottom);
  };

  useEffect(() => {
    const container = messageContainerRef.current;
    if (container) {
      container.addEventListener("scroll", handleScroll);
      return () => container.removeEventListener("scroll", handleScroll);
    }
  }, []);

  const [isUserScrolledUp, setIsUserScrolledUp] = useState(false);
  console.log("🚀  Chat  isUserScrolledUp:", isUserScrolledUp);

  return (
    <div className="flex flex-col h-full overflow-hidden bg-[#F0EEF8]">

      {/* ── TOP ACTION ROW: NUEVO CHAT button ── */}
      <div className="flex justify-end gap-2 px-4 pt-4 pb-2 flex-shrink-0">
        <button
          type="button"
          onClick={handleNewChatClick}
          className="btn-gradient-border flex items-center gap-1.5 sm:gap-2 px-2 sm:px-3 lg:px-4 py-2 font-bold text-dark-navy text-[10px] sm:text-xs uppercase tracking-wide whitespace-nowrap"
        >
          <CiRedo className="h-3.5 w-3.5 sm:h-4 sm:w-4 flex-shrink-0" />
          NUEVO CHAT
        </button>
      </div>

      {loading ? (
        <div className="flex justify-center items-center flex-1 w-full bg-[#F0EEF8]">
          <img src={Loader} alt="Loading..." className="h-16 w-16 mix-blend-multiply" />
        </div>
      ) : (
        <div className="flex-1 px-4 pb-3 min-h-0 flex flex-col gap-3 overflow-hidden">

          {/* ── WHITE CARD ── */}
          <div className="bg-white rounded-[27px] p-4 flex flex-col flex-1 min-h-0">

            {/* Card header: Upload button left + CHAT centred */}
            <div className="relative flex items-center justify-between w-full min-h-[47.6px] mb-3 flex-shrink-0">
              {/* Upload button */}
              {/* <div className="relative flex-shrink-0 flex items-center">
                <button
                  type="button"
                  className="flex items-center gap-[8.44px] h-[31px] w-[31px] sm:w-auto rounded-full sm:rounded-[67.52px] bg-[#3D2B90] text-white font-roboto font-semibold text-xs uppercase leading-none py-[12.66px] px-0 sm:px-[25.32px] justify-center whitespace-nowrap"
                >
                  <img src={UploadIcon} alt="upload" className="h-3.5 w-3.5 object-contain flex-shrink-0 brightness-0 invert" />
                  <span className="hidden sm:inline">UPLOAD FILE / IMAGE</span>
                </button>
              </div> */}

              {/* CHAT title — absolutely centred */}
              <span className="absolute left-1/2 -translate-x-1/2 font-roboto font-black text-[13px] sm:text-[14px] lg:text-[16px] text-[#42445A] uppercase leading-none tracking-normal text-center pointer-events-none whitespace-nowrap">
                CHAT
              </span>

              {/* Invisible spacer */}
              <div className="flex-shrink-0 w-[31px] sm:w-auto sm:px-[25.32px] invisible" aria-hidden="true">
                <span className="hidden sm:inline text-xs">UPLOAD FILE / IMAGE</span>
              </div>
            </div>

            {/* Content: messages panel + scrollbar */}
            <div className="flex flex-1 overflow-hidden min-h-0 w-full gap-3">

              {/* Left column: messages + input stacked */}
              <div className="flex flex-col flex-1 overflow-hidden min-w-0 gap-3">

                {/* Messages panel + custom scrollbar side by side */}
                <div className="flex gap-[10.18px] flex-1 min-h-0">
                  <div
                    ref={messageContainerRef}
                    onScroll={handleScroll}
                    className="bg-[#F3F1F9] rounded-[27px] p-3 flex flex-col flex-1 overflow-y-auto no-scrollbar relative min-h-0"
                  >
                    {initialRender ? (
                      <div className="flex flex-col items-center justify-center h-full gap-6" />
                    ) : (
                      <div className="grid gap-5 max-w-4xl w-full mx-auto py-2 pb-6">
                        {messages?.map((res, index) => (
                          <div
                            key={index}
                            className={`bg-white rounded-lg overflow-hidden relative tab:w-[calc(100%-100px)] w-full ${
                              res?.type === "bot"
                                ? "before-shadow ps-4 pt-5 pr-1 pb-1"
                                : "ms-auto p-4"
                            }`}
                          >
                            <div
                              className={`flex gap-3 items-start ${
                                res?.type === "bot" ? "text-left" : "flex-row-reverse text-right"
                              }`}
                            >
                              {res?.type === "bot" ? (
                                <div className="h-7 w-7 flex justify-center overflow-hidden rounded-full flex-shrink-0">
                                  <img src={Logo} alt="Linko" className="w-full h-full object-cover" />
                                </div>
                              ) : (
                                <div className="h-7 w-7 flex justify-center overflow-hidden rounded-full flex-shrink-0">
                                  <img src={UserProfile} alt="User" className="h-full w-full object-cover" />
                                </div>
                              )}
                              <div className="w-[calc(100%-56px)] selection:bg-white selection:text-black">
                                {res?.type === "bot" ? (
                                  <h5 className="font-semibold text-base mb-1 text-primary">LINKO</h5>
                                ) : (
                                  <h5 className="font-semibold text-base mb-1 text-primary capitalize">
                                    {user?.user?.name
                                      ? user?.user?.name
                                      : userDetails
                                      ? userDetails?.email?.match(/^[^@]+/)[0]
                                      : "John Doe"}
                                  </h5>
                                )}
                                <p className={`text-[#7376aa] ${res?.type === "bot" ? "customParagraph" : "whitespace-pre-wrap text-left"}`}>
                                  {res?.type === "bot" ? (
                                    <TypingResponse
                                      message={res?.content}
                                      setComplete={setComplete}
                                      isClick={isClick}
                                      isUserScrolledUp={isUserScrolledUp}
                                      setIsUserScrolledUp={setIsUserScrolledUp}
                                    />
                                  ) : (
                                    renderContent(res?.content)
                                  )}
                                </p>
                              </div>
                            </div>

                            {res?.type === "bot" && (
                              <div className="flex items-center justify-end gap-1 mt-1">
                                <button
                                  type="button"
                                  className="flex items-center justify-center gap-[8.44px] w-fit h-[31px] rounded-[67.52px] bg-[#3D2B90] text-white font-roboto font-semibold text-xs uppercase leading-none px-[25.32px] whitespace-nowrap mb-2"
                                  onClick={() => handleCopyClick(res?.content, index)}
                                >
                                  {index === copiedIndex ? (
                                    <>
                                      <BadgeCheckIcon className="h-3 w-3 text-white flex-shrink-0" />
                                      Copied
                                    </>
                                  ) : (
                                    <>
                                      <Copy className="h-3.5 w-3.5 flex-shrink-0" />
                                      Copy
                                    </>
                                  )}
                                </button>
                              </div>
                            )}
                          </div>
                        ))}

                        {isLoading && (
                          <div className="p-7 bg-white rounded-lg overflow-hidden relative before-shadow tab:w-[calc(100%-100px)]">
                            <div className="bouncing_loader"></div>
                          </div>
                        )}
                        <div ref={messageEndRef} />
                      </div>
                    )}
                  </div>

                  {/* Custom scrollbar track */}
                  <div className="relative w-[14px] lg:w-[17.94px] h-full bg-[#C7BFE4] rounded-[17px] flex-shrink-0 cursor-pointer" />
                </div>

                {/* Input toolbar */}
                <div className="flex items-center gap-3 flex-shrink-0" style={{ paddingRight: "calc(14px + 10.18px)" }}>
                  {selectedOptions && (
                    <div className="flex gap-2 flex-wrap px-1 w-full">
                      <div className="w-auto h-9 pl-3 pr-2 border border-none bg-violet-50 rounded-2xl flex items-center gap-2 text-sm text-black">
                        <span>{selectedOptions}</span>
                        <button
                          type="button"
                          onClick={() => handleRemoveOption(selectedOptions)}
                          className="text-primary text-2xl cursor-pointer pb-1"
                        >
                          &times;
                        </button>
                      </div>
                    </div>
                  )}

                  <form onSubmit={formik.handleSubmit} className="flex items-center gap-3 flex-1 min-w-0">
                    <div className="relative flex items-center flex-1 min-w-0 bg-[#F3F1F9] rounded-[22px] px-4 gap-3 h-[59.34px]">
                      {!formik.values.input && (
                        <span className="pointer-events-none absolute inset-0 flex items-center justify-center select-none">
                          <p className="text-[#3D2B90]/40 text-lg font-medium leading-snug text-center">
                            Type your message here...
                          </p>
                        </span>
                      )}
                      <textarea
                        ref={textareaRef}
                        name="input"
                        rows={1}
                        className="bg-transparent focus:outline-none border-0 w-full text-[#3D2B90] text-sm lg:text-base resize-none no-scrollbar self-center z-10"
                        value={formik.values.input}
                        onChange={formik.handleChange}
                        onBlur={(e) => {
                          const trimmedValue = e.target.value.replace(/^\s+|\s+$/g, "");
                          setTimeout(() => formik.setFieldValue("input", trimmedValue), 0);
                        }}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" && !e.shiftKey) {
                            e.preventDefault();
                            if (formik.values.input.trim() !== "") formik.handleSubmit();
                          }
                        }}
                      />
                      {(isLoading || complete) ? (
                        <button
                          type="button"
                          className="cursor-pointer flex-shrink-0"
                          style={{ width: "31.08px", height: "31.08px" }}
                          onClick={() => {
                            setIsClick(true);
                            setIsUserScrolledUp(false);
                            if (abortControllerRef.current) abortControllerRef.current.abort();
                          }}
                        >
                          <BsFillStopCircleFill size={28} className="text-[#3D2B90]" />
                        </button>
                      ) : (
                        <button
                          type="button"
                          className={`flex-shrink-0 ${formik.values.input.trim() ? "cursor-pointer" : "cursor-default opacity-40"}`}
                          style={{ width: "31.08px", height: "31.08px" }}
                          onClick={() => { if (formik.values.input.trim() !== "") formik.handleSubmit(); }}
                        >
                          <svg viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-full">
                            <circle cx="16" cy="16" r="15" stroke="#3D2B90" strokeWidth="2" />
                            <polygon points="13,10 23,16 13,22" fill="#3D2B90" />
                          </svg>
                        </button>
                      )}
                    </div>
                  </form>
                </div>

              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Chat;

export const TypingResponse = ({
  message,
  typingSpeed = 4,
  setComplete,
  isClick,
  isUserScrolledUp, // Pass this from parent
  setIsUserScrolledUp,
}) => {
  const [displayedText, setDisplayedText] = useState("");
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isTyping, setIsTyping] = useState(true);

  const containerRef = useRef(null);
  const endOfMessageRef = useRef(null);
  let typingTimer = useRef(null);
  let lastTime = useRef(Date.now());

  // Typing effect logic
  useEffect(() => {
    if (isClick) {
      setDisplayedText(displayedText);
      setIsTyping(false);
      setComplete(false);
      return;
    }

    if (currentIndex < message.length && isTyping) {
      setComplete(true);

      typingTimer.current = setTimeout(() => {
        setDisplayedText((prev) => prev + message[currentIndex]);
        setCurrentIndex((prev) => prev + 1);
      }, typingSpeed);
    } else {
      setIsTyping(false);
    }

    return () => clearTimeout(typingTimer.current);
  }, [currentIndex, message, typingSpeed, isTyping]);

  // Keep typing effect running in the background
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.hidden) {
        lastTime.current = Date.now();
      } else {
        const elapsedTime = Date.now() - lastTime.current;
        const skippedChars = Math.floor(elapsedTime / typingSpeed);
        setCurrentIndex((prev) =>
          Math.min(prev + skippedChars, message.length)
        );
        setDisplayedText(
          message.slice(
            0,
            Math.min(currentIndex + skippedChars, message.length)
          )
        );
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () =>
      document.removeEventListener("visibilitychange", handleVisibilityChange);
  }, [message, typingSpeed, currentIndex]);

  // Scroll to bottom only if user has NOT scrolled up
  useEffect(() => {
    if (!isUserScrolledUp && isTyping && endOfMessageRef.current) {
      endOfMessageRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [displayedText, isTyping, isUserScrolledUp]);

  // Set complete when message is fully displayed
  useEffect(() => {
    if (message.length === currentIndex) {
      setComplete(false);
    }
  }, [displayedText]);

  // Cleanup function
  useEffect(() => {
    return () => clearTimeout(typingTimer.current);
  }, []);

  return (
    <div
      ref={containerRef}
      className="overflow-auto rounded-lg customParagraph"
    >
      <div className="prose">{renderContent(displayedText)}</div>
      {/* Invisible element at the end of the message for smooth scrolling */}
      <div ref={endOfMessageRef} />
    </div>
  );
};
